import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Button,
  Breadcrumbs,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { Add as AddIcon, ArrowBack as BackIcon } from "@mui/icons-material";
import {
  MatchdayList,
  CreateMatchdayModal,
  EditMatchdayModal,
} from "../components/matchdays";
import { get, post, del, patch } from "../utils/networkUtils";
import { Link as RouterLink } from "react-router-dom";

const TournamentMatchdaysPage = () => {
  const { id: tournamentId } = useParams();
  const navigate = useNavigate();

  const [tournament, setTournament] = useState(null);
  const [matchdays, setMatchdays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    matchdayId: null,
  });
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedMatchday, setSelectedMatchday] = useState(null);

  const isWorldCup =
    tournament?.competition_name?.toLowerCase().includes("world cup") ||
    tournament?.competition_name?.toLowerCase().includes("fifa world");

  // Fetch tournament details
  const fetchTournament = async () => {
    try {
      const response = await get(`/admin/tournament-calendar/${tournamentId}`);
      if (response?.data) {
        setTournament(response.data);
      } else {
        setTournament(null);
      }
    } catch (error) {
      console.error("Failed to fetch tournament:", error);
      setTournament(null);
    }
  };

  // Fetch matchdays from API
  const fetchMatchdays = async () => {
    try {
      setLoading(true);
      const response = await get(
        `/admin/matchday?tournamentCalendarId=${tournamentId}&limit=100`
      );

      if (response?.data?.matchdays) {
        // Map backend format to frontend format
        const mappedMatchdays = response.data.matchdays.map((md) => ({
          id: md.id,
          tournamentId: md.tournamentCalendarId,
          matchdayNumber: md.matchdayNumber,
          name: md.name,
          expectedMatchCount: md.expectedMatchCount,
          assignedMatches: md.assignedMatches || 0,
          status: md.status,
          startsAt: md.startsAt,
          endsAt: md.endsAt,
          locksAt: md.locksAt,
          budgetInitial: md.budgetInitial,
          createdAt: md.createdAt,
          updatedAt: md.updatedAt,
        }));
        setMatchdays(mappedMatchdays);
      }
    } catch (error) {
      console.error("Failed to fetch matchdays:", error);
      setSnackbar({
        open: true,
        message: "Failed to load matchdays. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      await fetchTournament();
      await fetchMatchdays();
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId]);

  const handleCreateMatchday = () => {
    setCreateModalOpen(true);
  };

  const handleCreateSubmit = async (formData) => {
    if (!tournamentId) {
      setSnackbar({
        open: true,
        message: "Tournament not loaded yet. Please try again.",
        severity: "error",
      });
      return;
    }

    try {
      // Map frontend format to backend format
      // Note: startsAt, endsAt, locksAt are auto-calculated when matches are assigned
      const payload = {
        tournamentCalendarId: tournamentId,
        matchdayNumber: formData.matchdayNumber,
        name: formData.name,
        expectedMatchCount: formData.expectedMatchCount,
        status: formData.status || "upcoming",
        budgetInitial: formData.budgetInitial,
      };

      const response = await post("/admin/matchday/", payload);

      if (response?.data) {
        const newMatchday = {
          id: response.data.id,
          tournamentId: response.data.tournamentCalendarId,
          matchdayNumber: response.data.matchdayNumber,
          name: response.data.name,
          expectedMatchCount: response.data.expectedMatchCount,
          assignedMatches: response.data.assignedMatches || 0,
          status: response.data.status,
          startsAt: response.data.startsAt,
          endsAt: response.data.endsAt,
          locksAt: response.data.locksAt,
          budgetInitial: response.data.budgetInitial,
          createdAt: response.data.createdAt,
          updatedAt: response.data.updatedAt,
        };

        setMatchdays((prev) =>
          [...prev, newMatchday].sort(
            (a, b) => a.matchdayNumber - b.matchdayNumber
          )
        );
        setSnackbar({
          open: true,
          message: `Matchday "${formData.name}" created successfully`,
          severity: "success",
        });
        setCreateModalOpen(false);
      }
    } catch (error) {
      console.error("Failed to create matchday:", error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to create matchday",
        severity: "error",
      });
    }
  };

  const handleViewMatchday = (matchdayId) => {
    navigate(`/dashboard/tournaments/${tournamentId}/matchdays/${matchdayId}`);
  };

  const handleEditMatchday = (matchdayId) => {
    const matchday = matchdays.find((md) => md.id === matchdayId);
    if (matchday) {
      setSelectedMatchday(matchday);
      setEditModalOpen(true);
    }
  };

  const handleEditSubmit = async (formData) => {
    try {
      // Map frontend format to backend format
      const payload = {
        name: formData.name,
        expectedMatchCount: formData.expectedMatchCount,
        status: formData.status,
      };

      const response = await patch(
        `/admin/matchday/${selectedMatchday.id}`,
        payload
      );

      if (response?.data) {
        setMatchdays((prev) =>
          prev.map((md) =>
            md.id === selectedMatchday.id
              ? {
                  ...md,
                  name: response.data.name,
                  expectedMatchCount: response.data.expectedMatchCount,
                  status: response.data.status,
                  startsAt: response.data.startsAt,
                  endsAt: response.data.endsAt,
                  locksAt: response.data.locksAt,
                  updatedAt: response.data.updatedAt,
                }
              : md
          )
        );
        setSnackbar({
          open: true,
          message: `Matchday "${formData.name}" updated successfully`,
          severity: "success",
        });
        setEditModalOpen(false);
        setSelectedMatchday(null);
      }
    } catch (error) {
      console.error("Failed to update matchday:", error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to update matchday",
        severity: "error",
      });
    }
  };

  const handleAssignMatches = (matchdayId) => {
    navigate(
      `/dashboard/tournaments/${tournamentId}/matchday-builder?matchday=${matchdayId}`
    );
  };

  const handleStatusChange = async (matchdayId, newStatus) => {
    try {
      await patch(`/admin/matchday/${matchdayId}/status`, {
        status: newStatus,
      });

      setMatchdays((prev) =>
        prev.map((md) =>
          md.id === matchdayId ? { ...md, status: newStatus } : md
        )
      );

      const statusLabels = {
        upcoming: "Upcoming",
        active: "Active",
        locked: "Locked",
        completed: "Completed",
      };

      setSnackbar({
        open: true,
        message: `Matchday status changed to ${statusLabels[newStatus]}`,
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to update status:", error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to update status",
        severity: "error",
      });
    }
  };

  const handleDeleteClick = (matchdayId) => {
    setDeleteDialog({ open: true, matchdayId });
  };

  const handleDeleteConfirm = async () => {
    try {
      await del(`/admin/matchday/${deleteDialog.matchdayId}`);

      setMatchdays((prev) =>
        prev.filter((md) => md.id !== deleteDialog.matchdayId)
      );
      setSnackbar({
        open: true,
        message: "Matchday deleted successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to delete matchday:", error);
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Failed to delete matchday. It may have assigned matches.",
        severity: "error",
      });
    } finally {
      setDeleteDialog({ open: false, matchdayId: null });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const existingMatchdayNumbers = matchdays.map((md) => md.matchdayNumber);

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!tournament) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error">Tournament not found</Alert>
      </Container>
    );
  }

  if (!isWorldCup) {
    return (
      <Container maxWidth="xl">
        <Alert severity="warning" sx={{ mt: 4 }}>
          Matchday management is only available for World Cup tournaments.
        </Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(`/dashboard/tournaments/${tournamentId}`)}
          sx={{ mt: 2 }}
        >
          Back to Tournament
        </Button>
      </Container>
    );
  }

  const completedMatchdays = matchdays.filter(
    (md) => md.status === "completed"
  ).length;
  const activeMatchdays = matchdays.filter(
    (md) => md.status === "active"
  ).length;
  const upcomingMatchdays = matchdays.filter(
    (md) => md.status === "upcoming"
  ).length;

  return (
    <>
      <Helmet>
        <title>
          {tournament.competition_name} - Matchdays | DreamTeam Admin
        </title>
      </Helmet>

      <Container maxWidth="xl">
        <Breadcrumbs sx={{ mb: 2 }}>
          <Typography
            component={RouterLink}
            to="/dashboard/tournaments"
            sx={{
              color: "inherit",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Tournaments
          </Typography>
          <Typography
            component={RouterLink}
            to={`/dashboard/tournaments/${tournamentId}`}
            sx={{
              color: "inherit",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {tournament.competition_name}
          </Typography>
          <Typography color="text.primary">Matchdays</Typography>
        </Breadcrumbs>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h4">{tournament.competition_name}</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate(`/dashboard/tournaments/${tournamentId}`)}
            >
              Back
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateMatchday}
            >
              Create Matchday
            </Button>
          </Box>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Manage matchdays and match assignments for this tournament
        </Typography>

        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Total Matchdays
              </Typography>
              <Typography variant="h4">{matchdays.length}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Completed
              </Typography>
              <Typography variant="h4">{completedMatchdays}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Active
              </Typography>
              <Typography variant="h4">{activeMatchdays}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Upcoming
              </Typography>
              <Typography variant="h4">{upcomingMatchdays}</Typography>
            </CardContent>
          </Card>
        </Box>

        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Matchdays
            </Typography>
            <MatchdayList
              matchdays={matchdays}
              onView={handleViewMatchday}
              onEdit={handleEditMatchday}
              onDelete={handleDeleteClick}
              onStatusChange={handleStatusChange}
              onAssignMatches={handleAssignMatches}
              tournamentId={tournamentId}
            />
          </CardContent>
        </Card>
      </Container>

      {/* Create Matchday Modal */}
      <CreateMatchdayModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        tournamentId={tournamentId}
        existingMatchdayNumbers={existingMatchdayNumbers}
        onSubmit={handleCreateSubmit}
      />

      {/* Edit Matchday Modal */}
      <EditMatchdayModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        matchday={selectedMatchday}
        onSubmit={handleEditSubmit}
      />

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, matchdayId: null })}
      >
        <DialogTitle>Delete Matchday?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this matchday?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Matchdays with assigned matches cannot be deleted.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, matchdayId: null })}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TournamentMatchdaysPage;
